import type { Creator, FilterField, LibraryFilter } from '@/interfaces'
import type { Language, Languages, Tag } from '@/services/general/types'
import type { VideoCategories, VideoCategory } from '@/services/types'
import type { Video } from '@/services/videos/types'
import type { Package } from '@/services/content/package/types'
import { TRUVID_MAX_FILTER_VIDEO_LENGTH } from '@/services/constants'
import { Folder, Folders } from '@/services/folder/types'

export interface Library {
  filters: LibraryFilter
  languages: Languages
  categories: VideoCategories
  tags: Tag[]
  videos: Video[]
  currentPageNumber: number
  categoriesArray: VideoCategory[]
  languagesArray: Language[]
  packages: Package[]
  creators: Creator[]
  folders: Folders
}

export function getDefaultFilters(): LibraryFilter {
  return {
    packages: [],
    ownership: false,
    is_private: false,
    show_packages: false,
    show_folders: false,
    show_public: false,
    duration: [0, TRUVID_MAX_FILTER_VIDEO_LENGTH],
    categories: [],
    tags: [],
    creatorIds: [],
    search: '',
    languages: [],
    sortBy: '',
    sortOrder: '',
    results_per_page: 0,
    page_number: 1,
    startDate: '',
    endDate: '',
    pricing: '',
    uploadDate: 'all',
    orientation: 'all',
    folderId: 0,
    source_type: ''
  }
}

export const library = {
  namespaced: true,
  state: (): Library => ({
    filters: getDefaultFilters(),
    languages: { languages: [] },
    categories: { categories: [] },
    tags: [],
    packages: [],
    creators: [],
    folders: { folders: [], total: 0 },
    currentPageNumber: 1,
    categoriesArray: [],
    languagesArray: [],
    videos: []
  }),
  mutations: {
    setPackagesFilter(state, packages: number[]) {
      state.filters.packages = packages
    },
    setOwnership(state, isOwn: boolean) {
      state.filters.ownership = isOwn
    },
    setPrivacy(state, isPrivate: boolean) {
      state.filters.is_private = isPrivate
    },
    setIsShowPublic(state, isShowPublic: boolean) {
      state.filters.show_public = isShowPublic
    },
    setIsShowPackages(state, isShowPackages: boolean) {
      state.filters.show_packages = isShowPackages
    },
    setIsShowFolders(state, isShowFolders: boolean) {
      state.filters.show_folders = isShowFolders
    },
    setDurationFilter(state, duration: number[]) {
      state.filters.duration = duration
    },
    setCurrentPageNumber(state, page: number) {
      state.currentPageNumber = page
    },
    setCategories(state, categories: number[]) {
      state.filters.categories = categories
    },
    setTags(state, tags: number[]) {
      state.filters.tags = tags
    },
    setCreators(state, creatorIds: number[]) {
      state.filters.creatorIds = creatorIds
    },
    setFolderFilter(state, id: number) {
      state.filters.folderId = id
    },
    setSearch(state, search: string) {
      state.filters.search = search
    },
    setUploadStartDate(state, startDate: string) {
      state.filters.startDate = startDate
    },
    setUploadEndDate(state, startDate: string) {
      state.filters.endDate = startDate
    },
    setLanguages(state, languages: number[]) {
      state.filters.languages = languages
    },
    setSort(state, sort: { sortBy: string; sortOrder: string }) {
      state.filters.sortBy = sort.sortBy
      state.filters.sortOrder = sort.sortOrder
    },
    setPricing(state, pricing: string) {
      state.filters.pricing = pricing
    },
    setUploadDate(state, date: string) {
      state.filters.uploadDate = date
    },
    setOrientationFilter(state, orientation: string) {
      state.filters.orientation = orientation
    },
    setSourceType(state, sourceType: string) {
      state.filters.source_type = sourceType
    },
    setAvailableCategoriesAndLanguagesAndCreatorsAndPackages(
      state: Library,
      obj: { categories: VideoCategories; languages: Languages; creators: Creator[]; packages: Package[]; folders: Folders; tags: Tag[] }
    ) {
      state.categories = obj.categories
      state.languages = obj.languages
      state.creators = obj.creators
      state.packages = obj.packages
      state.folders = obj.folders
      state.tags = obj.tags
    },
    setPackages(state, packages: Package[]) {
      state.packages = packages
    },
    setVideos(state, videos: Video[]) {
      state.videos = videos
    },
    setCategoriesArray(state, categories: VideoCategory[]) {
      state.categoriesArray = categories
    },
    setPackagesArray(state, packages: VideoCategory[]) {
      state.packgaesArray = packages
    },
    setLanguagesArray(state, languages: Language[]) {
      state.languagesArray = languages
    },
    selectVideo(state, video: Video) {
      state.selectedVideos.push(video)
    },
    setSelectVideos(state, videos: Video[]) {
      state.selectedVideos = videos
    },
    deselectAllVideos(state) {
      state.selectedVideos = []
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFilter(state, payload: { filterName: FilterField; value: any }) {
      const { filterName, value } = payload
      state.filters[`${filterName as string}`] = value
    },
    updateVideo(state, payload: { index: number; updatedVideo: Video }) {
      state.videos.splice(payload.index, 1, payload.updatedVideo)
    },
    logout(state) {
      state.filters = getDefaultFilters()
      state.languages = { languages: [] }
      state.categories = { categories: [] }
      state.tags = []
      state.creators = { creators: [] }
      state.currentPageNumber = 1
      state.videos = []
      state.categoriesArray = []
      state.languagesArray = []
    },
    resetFilters(state: Library) {
      state.filters = getDefaultFilters()
    },
    setFilters(state: Library, filters: LibraryFilter) {
      state.filters = { ...filters }
    }
  },
  getters: {
    getFolders: (state: Library) => (): Folder[] => {
      return state.folders.folders
    },
    getFolderFilter: (state: Library) => (): number => {
      return state.filters.folderId
    },
    getShowFoldersFilter: (state: Library) => (): boolean => {
      return state.filters.show_folders
    },
    getShowPackagesFilter: (state: Library) => (): boolean => {
      return state.filters.show_packages
    },
    getShowPublicFilter: (state: Library) => (): boolean => {
      return state.filters.show_public
    },
    getOwnershipFilter: (state: Library) => (): boolean => {
      return state.filters.ownership
    },
    getSourceTypeFilter: (state: Library) => (): string => {
      return state.filters.source_type
    }
  },
  actions: {
    setPackages({ commit }, packages: Package[]) {
      commit('setPackages', packages)
    }
  }
}
